import { useState, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { createSession, resolveInstance } from "../services/authSessionService";
import { setPermissions, setCapabilities, setIsFullAccess } from "../store/slices/authSlice";
import { logger } from "../services/logger.service";
import { authService } from "../utils/authService";
import type { ResolveInstanceResponse } from "../../shared/types/rbac";
import {
  denialCodeOf,
  denialFromResolve,
  reportAccessDenial,
  type AccessDenial,
} from "../components/access/accessDenial";
import { reloadIntoInstance } from "./reloadIntoInstance";

interface UseAppInitializationProps {
  currentUserId: string | undefined;
  getQueryString: () => string;
  fetchSessionConfig: () => Promise<void>;
}

interface UseAppInitializationReturn {
  isAppLoaded: boolean;
  isCheckResourceFailed: boolean;
  isShowCreateTenant: boolean;
  createOwnInfrastructure: boolean;
  showExternalTenantCreationError: boolean;
  instanceId: string;
  userAccesstoInstance: boolean;
  setInstanceId: (id: string) => void;
  checkIfResourceExist: (urlInstanceId?: string) => void;
  hasAccessToInstance: (instanceId: string, options?: { remembered?: boolean }) => void;
  loadInitialAppData: (instanceId: string) => void;
  getUserAccess: (options?: { shouldSetAppLoaded?: boolean }) => Promise<boolean>;
  hideExternalTenantCreationError: () => void;
  setIsShowCreateTenant: (value: boolean) => void;
  setCreateOwnInfrastructure: (value: boolean) => void;
  setShowExternalTenantCreationError: (value: boolean) => void;
  setIsCheckResourceFailed: (value: boolean) => void;
  setUserAccessToInstance: (value: boolean) => void;
}

export const useAppInitialization = ({
  currentUserId,
  getQueryString,
  fetchSessionConfig,
}: UseAppInitializationProps): UseAppInitializationReturn => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isAppLoaded, setIsAppLoaded] = useState(false);
  const [isCheckResourceFailed, setIsCheckResourceFailed] = useState(true);
  const [isShowCreateTenant, setIsShowCreateTenant] = useState(false);
  const [createOwnInfrastructure, setCreateOwnInfrastructure] = useState(false);
  const [showExternalTenantCreationError, setShowExternalTenantCreationError] = useState(false);
  const [instanceId, setInstanceId] = useState("");
  const [userAccesstoInstance, setUserAccessToInstance] = useState(true);

  // Cache resolved instance data to avoid duplicate resolveInstance API calls
  const resolveCache = useRef<Map<string, ResolveInstanceResponse>>(new Map());

  const cachedResolveInstance = useCallback(
    async (id: string): Promise<ResolveInstanceResponse> => {
      const cached = resolveCache.current.get(id);
      if (cached) return cached;
      const result = await resolveInstance(id);
      resolveCache.current.set(id, result);
      return result;
    },
    [],
  );

  const applyPermissions = useCallback(
    (data: Pick<ResolveInstanceResponse, "flags" | "products" | "productRoles">) => {
      const { flags } = data;
      dispatch(
        setPermissions({
          isPlatformAdmin: flags.isSkyPointAdmin,
          isTenantAdmin: flags.isAdmin,
          isInstanceAdmin: flags.isAdmin,
        }),
      );
      dispatch(
        setCapabilities({
          allowView: flags.allowView,
          allowEdit: flags.allowEdit,
          allowDelete: flags.allowDelete,
          skyAgentEnabled: flags.skyAgentEnabled,
          // Both the real answer and the grantFullAccess fallback pass through here, so both resolve.
          productsResolved: true,
          products: data.products ?? [],
          productRoles: data.productRoles ?? {},
        }),
      );
      dispatch(setIsFullAccess(true));
    },
    [dispatch],
  );

  const grantFullAccess = useCallback(() => {
    applyPermissions({
      flags: {
        isAdmin: true,
        isSkyPointAdmin: false,
        allowView: true,
        allowEdit: true,
        allowDelete: true,
        // Full access is about what this user may do, not about which product
        // features the instance has bought. SkyAgent stays off unless the
        // instance is actually enabled for it.
        skyAgentEnabled: false,
      },
      // Products are what the instance bought, not what this user may do; never invented here.
      products: [],
      productRoles: {},
    });
  }, [applyPermissions]);

  const getUserAccess = useCallback(
    async (options?: { shouldSetAppLoaded?: boolean }): Promise<boolean> => {
      const { shouldSetAppLoaded = true } = options || {};

      if (!currentUserId) {
        logger.warn("getUserAccess called without authenticated user", {
          component: "useAppInitialization",
          action: "getUserAccess",
        });
        if (shouldSetAppLoaded) {
          setIsAppLoaded(true);
        }
        return false;
      }

      try {
        const params = new URLSearchParams(window.location.search);
        const currentInstanceId = params.get("instanceid") || instanceId;

        if (currentInstanceId) {
          const data = await cachedResolveInstance(currentInstanceId);
          if (data.hasAccess) {
            applyPermissions(data);
          } else {
            grantFullAccess();
          }
        } else {
          grantFullAccess();
        }
      } catch (error) {
        logger.error("Failed to resolve instance access, granting full access", error, {
          component: "useAppInitialization",
          action: "getUserAccess",
        });
        grantFullAccess();
      }

      if (shouldSetAppLoaded) {
        setIsAppLoaded(true);
      }

      return true;
    },
    [currentUserId, instanceId, applyPermissions, grantFullAccess, cachedResolveInstance],
  );

  const loadInitialAppData = useCallback(
    (targetInstanceId: string) => {
      getUserAccess({ shouldSetAppLoaded: false })
        .then(() => {
          setIsAppLoaded(true);
        })
        .catch((error) => {
          logger.error("Critical initialization failed, showing app anyway", error, {
            component: "useAppInitialization",
            action: "loadInitialAppData",
          });
          setIsAppLoaded(true);
        });

      // Session config is non-critical - runs in parallel without blocking UI
      fetchSessionConfig().catch((error) => {
        logger.error("Failed to fetch session config", error, {
          component: "useAppInitialization",
          action: "loadInitialAppData",
        });
      });
    },
    [getUserAccess, fetchSessionConfig],
  );

  const redirectToPage = useCallback(
    (urlInstanceId: string) => {
      const targetInstanceId = urlInstanceId || instanceId;
      if (isCheckResourceFailed) {
        loadInitialAppData(targetInstanceId);
      } else {
        setIsCheckResourceFailed(true);
        setTimeout(() => {
          loadInitialAppData(targetInstanceId);
        }, 5000);
      }
    },
    [instanceId, isCheckResourceFailed, loadInitialAppData],
  );

  // `refused`: the instance just refused, never reloaded into again (that reload
  // would be refused too, and again), and what to report when nothing else is open.
  const getDefaultInstance = useCallback(
    (refused?: AccessDenial) => {
      if (!currentUserId) {
        logger.error("Cannot get default instance: user ID is missing", undefined, {
          component: "useAppInitialization",
          action: "getDefaultInstance",
        });
        return;
      }

      createSession().then(
        (session) => {
          const firstInstance = session.accessibleInstances.find(
            (option) => option.instanceId !== refused?.instanceId,
          );
          if (firstInstance) {
            const defaultInstanceId = firstInstance.instanceId;
            setInstanceId(defaultInstanceId);
            reloadIntoInstance(defaultInstanceId);
          } else {
            logger.warn("No accessible instances found — showing no-access screen", {
              component: "useAppInitialization",
              action: "getDefaultInstance",
            });
            reportAccessDenial(refused ?? { code: "NO_ASSIGNMENT" });
          }
        },
        (error) => {
          logger.error("Failed to create session for default instance", error, {
            component: "useAppInitialization",
            action: "getDefaultInstance",
          });
          // A blocked account says so; anything else is a check that didn't complete.
          reportAccessDenial({ code: denialCodeOf(error) ?? "CHECK_FAILED" });
        },
      );
    },
    [currentUserId],
  );

  const checkIfResourceExist = useCallback(
    (urlInstanceId?: string) => {
      // Local resources always exist — proceed directly
      if (!urlInstanceId) {
        getDefaultInstance();
      } else {
        redirectToPage(urlInstanceId);
      }
    },
    [getDefaultInstance, redirectToPage],
  );

  /**
   * `remembered`: the instance came from the user's saved choice, not a link. A
   * link the user can't open ends on the no-access screen; a remembered one they've
   * lost gives way, once, to one they can open.
   */
  const hasAccessToInstance = useCallback(
    (targetInstanceId: string, options?: { remembered?: boolean }) => {
      if (!currentUserId) {
        setUserAccessToInstance(false);
        return;
      }

      cachedResolveInstance(targetInstanceId).then(
        (data) => {
          const code = denialFromResolve(data);
          if (!code) {
            applyPermissions(data);
            checkIfResourceExist(targetInstanceId);
            return;
          }
          const denial: AccessDenial = { code, instanceId: targetInstanceId };
          logger.warn("Instance is inaccessible", {
            component: "useAppInitialization",
            action: "hasAccessToInstance",
            instanceId: targetInstanceId,
            code,
          });
          if (code !== "NO_ASSIGNMENT" && code !== "INSTANCE_NOT_FOUND") {
            reportAccessDenial(denial);
            return;
          }
          // Don't come back here on the next sign-in.
          authService.clearInstanceSelection();
          if (options?.remembered) {
            getDefaultInstance(denial);
          } else {
            reportAccessDenial(denial);
          }
        },
        (error) => {
          logger.error("Failed to resolve instance access", error, {
            component: "useAppInitialization",
            action: "hasAccessToInstance",
            instanceId: targetInstanceId,
          });
          // Never a spinner that doesn't stop: a blocked account says so, and
          // anything else offers Try again.
          reportAccessDenial({
            code: denialCodeOf(error) ?? "CHECK_FAILED",
            instanceId: targetInstanceId,
          });
        },
      );
    },
    [
      currentUserId,
      checkIfResourceExist,
      getDefaultInstance,
      applyPermissions,
      cachedResolveInstance,
    ],
  );

  const hideExternalTenantCreationErrorFn = useCallback(() => {
    setShowExternalTenantCreationError(false);
    checkIfResourceExist(instanceId);
  }, [instanceId, checkIfResourceExist]);

  return {
    isAppLoaded,
    isCheckResourceFailed,
    isShowCreateTenant,
    createOwnInfrastructure,
    showExternalTenantCreationError,
    instanceId,
    userAccesstoInstance,
    setInstanceId,
    checkIfResourceExist,
    hasAccessToInstance,
    loadInitialAppData,
    getUserAccess,
    hideExternalTenantCreationError: hideExternalTenantCreationErrorFn,
    setIsShowCreateTenant,
    setCreateOwnInfrastructure,
    setShowExternalTenantCreationError,
    setIsCheckResourceFailed,
    setUserAccessToInstance,
  };
};
